// src/features/portfolio-optimization-tools/my-strategies/components/ExcessReturnBarChart.tsx
//
// The "alpha attribution" bar chart: for every calendar month it plots the
// strategy's EXCESS (active) return over the benchmark — green bars are months
// the daemon beat the benchmark, red bars are months it lagged. A dashed
// vertical marker flags where live out-of-sample tracking began, so the reader
// can see the alpha persist into real, forward-tracked money.

import { useMemo } from 'react';
import {
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    ReferenceLine,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';

import type {
    EquityCurvePoint,
    MonthlyExcessAlphaPoint,
} from '../../multi-asset-portfolio-optimizer/api';
import {
    computeMonthlyExcessReturns,
    rebaseToTWRR,
    type PeriodExcess,
} from './performanceMath';

const POSITIVE = '#10b981';
const NEGATIVE = '#ef4444';

const pct = (v: number): string => `${(v * 100).toFixed(2)}%`;

/** Current-month bucket key ('YYYY-MM') in the viewer's local calendar. */
const currentMonthKey = (): string => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

/** The month immediately after a 'YYYY-MM' key. */
const nextMonthKey = (period: string): string => {
    const [year, month] = period.split('-').map(Number);
    // `month` is 1-based, so passing it as a 0-based index yields next month.
    const d = new Date(year, month, 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

/** Human label for a 'YYYY-MM' key, e.g. "Sep 26". */
const monthLabel = (period: string): string => {
    const [year, month] = period.split('-');
    return new Date(Number(year), Number(month) - 1, 1).toLocaleDateString(
        'en-US',
        { month: 'short', year: '2-digit' },
    );
};

interface BarTooltipProps {
    active?: boolean;
    payload?: { payload: PeriodExcess }[];
    benchmarkName?: string;
}

const BarTooltip = ({
    active,
    payload,
    benchmarkName = 'Benchmark',
}: BarTooltipProps) => {
    if (!active || !payload?.length) return null;
    const d = payload[0].payload;
    // Synthetic current-month placeholder carries no real data.
    if (d.isPlaceholder) return null;
    const winning = d.excess >= 0;
    return (
        <div className="bg-slate-900 border border-slate-700 p-4 rounded-xl shadow-2xl min-w-[240px] z-50">
            <div className="flex justify-between items-center border-b border-slate-800 pb-2 mb-3">
                <p className="text-slate-400 text-xs font-bold uppercase tracking-wider">
                    {d.label}
                </p>
                {d.isLive && (
                    <span className="text-[9px] font-bold bg-indigo-500/20 text-indigo-400 border border-indigo-500/30 px-2 py-0.5 rounded uppercase tracking-wider">
                        LIVE OOS
                    </span>
                )}
            </div>
            <div className="space-y-2">
                <div className="flex justify-between items-center gap-4">
                    <span className="text-emerald-400 text-xs font-bold">
                        Strategy
                    </span>
                    <span className="text-white font-mono font-bold text-sm">
                        {pct(d.portReturn)}
                    </span>
                </div>
                <div className="flex justify-between items-center gap-4">
                    <span className="text-amber-400 text-xs font-semibold">
                        {benchmarkName}
                    </span>
                    <span className="text-slate-300 font-mono text-xs">
                        {pct(d.benchReturn)}
                    </span>
                </div>
                <div className="h-px w-full bg-slate-800 my-2" />
                <div className="flex justify-between items-center gap-4 bg-slate-950 p-2 rounded-lg border border-slate-800">
                    <span className="text-slate-300 text-[11px] font-bold uppercase tracking-wider">
                        Excess Alpha
                    </span>
                    <span
                        className={`font-mono font-black text-sm ${
                            winning ? 'text-emerald-400' : 'text-red-400'
                        }`}
                    >
                        {winning ? '+' : ''}
                        {pct(d.excess)}
                    </span>
                </div>
            </div>
        </div>
    );
};

interface Props {
    portfolioCurve: (EquityCurvePoint & { is_out_of_sample?: boolean })[];
    benchmarkCurve: (EquityCurvePoint & { is_out_of_sample?: boolean })[];
    benchmarkName?: string;
    /**
     * Optional stateful alpha straight from the Robo-Advisor. When present it
     * bypasses the static frontend TWRR math so the chart visualises the true
     * dynamic AI decisions pushing the green bars up.
     */
    backendAlpha?: MonthlyExcessAlphaPoint[];
}

export const ExcessReturnBarChart = ({
    portfolioCurve,
    benchmarkCurve,
    benchmarkName = 'Benchmark',
    backendAlpha,
}: Props) => {
    const data = useMemo<PeriodExcess[]>(() => {
        // The true live/out-of-sample boundary lives on the equity curve
        // (``is_out_of_sample`` = replayed day strictly after the strategy's
        // creation date). Derive the earliest live MONTH ('YYYY-MM') so the
        // LIVE marker sits at the real backtest->live transition, NOT at the
        // oldest bar. Robust to unordered curves (scans for the minimum).
        let liveStartMonth: string | null = null;
        for (const p of portfolioCurve) {
            if (p.is_out_of_sample && p.date) {
                const m = p.date.slice(0, 7); // 'YYYY-MM'
                if (liveStartMonth === null || m < liveStartMonth) {
                    liveStartMonth = m;
                }
            }
        }

        // OVERRIDE: if the Robo-Advisor provides explicit dynamic alpha, use it.
        const rows: PeriodExcess[] =
            backendAlpha && backendAlpha.length > 0
                ? backendAlpha.map((a) => {
                      const [year, month] = a.month.split('-');
                      const label = new Date(
                          Number(year),
                          Number(month) - 1,
                          1,
                      ).toLocaleDateString('en-US', {
                          month: 'short',
                          year: '2-digit',
                      });

                      return {
                          period: a.month,
                          label,
                          portReturn: a.port_return,
                          benchReturn: a.bench_return,
                          excess: a.excess,
                          // A month is LIVE only once it is at/after the real
                          // out-of-sample boundary; earlier months are backtest.
                          // If the curve has no OOS data, nothing is flagged
                          // live (so no marker is drawn on the far left).
                          isLive:
                              liveStartMonth !== null &&
                              a.month >= liveStartMonth,
                      };
                  })
                : // FALLBACK: pure frontend math for static / unmanaged reports.
                  computeMonthlyExcessReturns(
                      rebaseToTWRR(portfolioCurve),
                      rebaseToTWRR(benchmarkCurve),
                  );

        // Guarantee strict chronological order so the timeline always reads
        // oldest -> newest left-to-right. `period` is 'YYYY-MM', which sorts
        // lexicographically == chronologically. Without this, an unordered
        // backend `monthly_excess_alpha` array could render the most recent
        // (LIVE) months on the LEFT of the chart.
        const sorted = [...rows].sort((a, b) =>
            a.period.localeCompare(b.period),
        );

        // Always extend the timeline to the CURRENT calendar month so the
        // right edge of the X-axis reflects "today" and the strategy feels
        // live. The daemon only writes a month once it has data, so when the
        // current month has not been tracked yet the last real bar can be a
        // month (or more) in the past. Pad the gap with zero-value placeholder
        // buckets: they render no bar (excess = 0) and no tooltip, but their
        // month label still appears on the axis.
        if (sorted.length > 0) {
            const target = currentMonthKey();
            let cursor = sorted[sorted.length - 1].period;
            while (cursor < target) {
                cursor = nextMonthKey(cursor);
                sorted.push({
                    period: cursor,
                    label: monthLabel(cursor),
                    portReturn: 0,
                    benchReturn: 0,
                    excess: 0,
                    isLive:
                        liveStartMonth !== null && cursor >= liveStartMonth,
                    isPlaceholder: true,
                });
            }
        }

        return sorted;
    }, [portfolioCurve, benchmarkCurve, backendAlpha]);

    // First month that falls inside the live out-of-sample window, if any.
    const liveStartLabel = useMemo(
        () => data.find((d) => d.isLive)?.label ?? null,
        [data],
    );

    // Aggregate stats for the sub-header: share of REAL (non-placeholder)
    // months with positive alpha. Synthetic current-month padding buckets are
    // excluded so the ratio reflects genuinely tracked performance.
    const realMonths = useMemo(
        () => data.filter((d) => !d.isPlaceholder),
        [data],
    );
    const winMonths = useMemo(
        () => realMonths.filter((d) => d.excess >= 0).length,
        [realMonths],
    );

    if (data.length === 0) {
        return null;
    }

    return (
        <div className="w-full">
            <p className="muted text-[11px] mb-3">
                <span className="text-emerald-500 font-bold">
                    {winMonths}/{realMonths.length}
                </span>{' '}
                months of positive alpha vs {benchmarkName}.{' '}
                <span className="text-emerald-500 font-semibold">Green</span> =
                strategy beat the benchmark that month,{' '}
                <span className="text-red-500 font-semibold">red</span> = lagged.
            </p>
            <div className="w-full h-[240px]">
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                        data={data}
                        margin={{ top: 8, right: 8, left: 10, bottom: 0 }}
                    >
                        <CartesianGrid
                            strokeDasharray="3 3"
                            vertical={false}
                            stroke="#334155"
                            opacity={0.2}
                        />
                        <XAxis
                            dataKey="label"
                            stroke="#64748b"
                            fontSize={11}
                            tickMargin={10}
                            minTickGap={40}
                            axisLine={false}
                            tickLine={false}
                        />
                        <YAxis
                            tickFormatter={(v: number) =>
                                `${(v * 100).toFixed(0)}%`
                            }
                            stroke="#64748b"
                            fontSize={11}
                            axisLine={false}
                            tickLine={false}
                            width={44}
                        />
                        <Tooltip
                            content={
                                <BarTooltip benchmarkName={benchmarkName} />
                            }
                            cursor={{ fill: '#64748b', fillOpacity: 0.08 }}
                        />
                        <ReferenceLine y={0} stroke="#475569" />
                        {liveStartLabel && (
                            <ReferenceLine
                                x={liveStartLabel}
                                stroke="#818cf8"
                                strokeDasharray="4 4"
                                label={{
                                    position: 'insideTopRight',
                                    value: 'LIVE ',
                                    fill: '#818cf8',
                                    fontSize: 10,
                                    fontWeight: 'bold',
                                }}
                            />
                        )}
                        <Bar
                            dataKey="excess"
                            radius={[2, 2, 0, 0]}
                            isAnimationActive
                            animationDuration={900}
                        >
                            {data.map((d) => (
                                <Cell
                                    key={d.period}
                                    fill={d.excess >= 0 ? POSITIVE : NEGATIVE}
                                    fillOpacity={d.isLive ? 1 : 0.7}
                                />
                            ))}
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
};

export default ExcessReturnBarChart;
